import { useEffect, useState } from "react";

import { prefetchEclaimEntityLookups } from "@/api/eclaim-entity-search/prefetchEclaimEntityLookups";
import { taskWithStepsQueryOptions } from "@/api/tasks/taskWithStepsQueryOptions";
import { StepRenderer } from "@/components/steps/StepRenderer";
import { BucketFinishProvider } from "@/components/steps/shared/BucketFinishContext";
import { TaskBanner } from "@/components/steps/shared/TaskBanner";
import { useQueryClient, useQueryErrorResetBoundary, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";
import { ZodError, z } from "zod";

import type { StepWithType } from "@workspace/schemas/zod";
import { Button } from "@workspace/ui/components/button";

const taskIdSchema = z.coerce.number().int().positive();

export const Route = createFileRoute("/_standard/tasks/$taskId/")({
  params: {
    parse: (params) => ({
      taskId: taskIdSchema.parse(params.taskId),
    }),
  },
  loader: async ({ context: { queryClient }, params: { taskId } }) => {
    // Prefetch task data - TanStack Query handles caching and invalidation
    // API performs authorization checks (all-buckets role OR task assignee)
    const task = await queryClient.ensureQueryData(taskWithStepsQueryOptions(taskId));
    // Fire-and-forget cache warming: the check step form's entity lookups (labels,
    // suggestion verification) hit warm cache instead of rendering a pending state.
    prefetchEclaimEntityLookups(queryClient, task.steps);
    return task;
  },
  component: RouteComponent,
  errorComponent: ErrorComponent,
});

// Find the current step that needs user action
// Skip system steps (responsibleAgent !== "user") and completed/failed steps
function findCurrentUserStep(steps: StepWithType[]) {
  return steps.find((step) => step.stepTypeInfo.responsibleAgent === "user" && (step.status === "pending" || step.status === "in_progress"));
}

// Get user-facing steps only (for progress indicator)
function getUserSteps(steps: StepWithType[]) {
  return steps.filter((step) => step.stepTypeInfo.responsibleAgent === "user");
}

function RouteComponent() {
  const { taskId } = useParams({ from: "/_standard/tasks/$taskId/" });
  const { data: task } = useSuspenseQuery(taskWithStepsQueryOptions(taskId));
  const queryClient = useQueryClient();

  // Get user-facing steps for display
  const userSteps = getUserSteps(task.steps);

  // Find the current step that needs user action
  const currentStep = findCurrentUserStep(task.steps);

  // Track which step is being viewed (for manual navigation)
  const [viewedStepId, setViewedStepId] = useState<number | null>(null);

  // Reset viewed step when task changes (render-time update avoids cascading effect renders)
  const [prevTaskId, setPrevTaskId] = useState(taskId);
  if (prevTaskId !== taskId) {
    setPrevTaskId(taskId);
    setViewedStepId(null);
  }

  // The step to display: either the manually selected one or the current pending step
  const displayedStep = viewedStepId !== null ? (userSteps.find((s) => s.id === viewedStepId) ?? currentStep) : currentStep;

  if (!currentStep && userSteps.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 p-10">
          <div className="rounded-lg border bg-muted p-4">
            <p className="text-muted-foreground">Keine Steps für diesen Task gefunden.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!displayedStep) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 p-10">
          <div className="rounded-lg border border-success/50 bg-success/10 p-4">
            <p className="text-success">Alle Steps wurden abgeschlossen.</p>
          </div>
        </div>
      </div>
    );
  }

  const transitionContext = {
    stepTypeKey: displayedStep.stepTypeInfo.key,
    stepId: displayedStep.id,
    taskId,
    bucketId: task.bucket ?? null,
    onTransitionComplete: async () => {
      setViewedStepId(null);

      await queryClient.invalidateQueries({
        queryKey: taskWithStepsQueryOptions(taskId).queryKey,
      });
    },
  };

  return (
    // BucketFinishProvider wraps the banner + step renderer so the active step component can
    // publish form-state-aware completion data that FinishBucketAction reads. `key={taskId}`
    // forces a remount on task change so the context state resets — the route component
    // itself doesn't remount (it stays mounted while the search params / pathname change).
    <BucketFinishProvider key={taskId}>
      <div className="flex min-h-0 flex-1 flex-col">
        <TaskBanner task={task} displayedStep={displayedStep} />
        {/* Main content area */}
        <div className="flex min-h-0 flex-1 flex-col overflow-auto px-6 py-4">
          {/* Step renderer - pass input data! */}
          <div className="flex min-h-0 flex-1 flex-col">
            <StepRenderer
              transitionContext={transitionContext}
              input={displayedStep.input}
              currentResult={displayedStep.result}
              disabled={displayedStep.status === "success"}
            />
          </div>
        </div>
      </div>
    </BucketFinishProvider>
  );
}

function ErrorComponent({ error }: { error: Error }) {
  const { reset: tanStackReset } = useQueryErrorResetBoundary();
  const router = useRouter();

  useEffect(() => {
    // In Suspense, React Query disables retry-on-mount after an error until the error boundary has been reset to prevent error loops; QueryErrorResetBoundary provides that reset signal.
    // Marks queries inside the boundary as "reset," allowing useSuspenseQuery to retry on the next render instead of rethrowing the cached error; it doesn't invalidate or refetch on its own.
    tanStackReset();
  }, [tanStackReset]);

  if (error instanceof ZodError) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 p-10">
        {/* Makes no sense to offer retry button if task id is invalid */}
        <p>Ungültige Task-ID</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 p-10">
      <p>Ein Fehler ist aufgetreten</p>
      <Button
        onClick={() => {
          // A stale chunk (deleted by a newer deploy) is cached as a permanently-rejected lazy import,
          // so router.invalidate() re-throws it without re-fetching — only a full reload recovers.
          if (isChunkLoadError(error)) {
            window.location.reload();
            return;
          }
          // Otherwise the error came from a route load; router.invalidate() coordinates both a router
          // reload and an error boundary reset.
          void router.invalidate();
        }}
      >
        Erneut versuchen
      </Button>
    </div>
  );
}

// Chrome / Firefox / Safari phrasings for a failed dynamic import (stale chunk after a deploy).
function isChunkLoadError(error: Error) {
  return /dynamically imported module|Importing a module script failed/i.test(error.message);
}
